import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { AssistantPanel } from '@/features/assistant';
import { CommandPalette } from '@/features/command-center';
import { useSession } from '@/stores/session';
import { AppFooter } from './AppFooter';
import { Header } from './Header';
import { useNavCollapse } from './navCollapse';
import { SidebarNav } from './SidebarNav';
import { SidebarRail } from './SidebarRail';

/**
 * Authenticated app shell: sticky top header, a persistent sidebar on `lg+` that
 * collapses to a slide-in drawer on smaller screens, and the routed content in
 * <Outlet />. Responsive per FE-ADR-008.
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const sidebarCollapsed = useNavCollapse((s) => s.sidebarCollapsed);
  const activeSlug = useSession((s) => s.activeOrgSlug);
  const orgs = useSession((s) => s.organizations);
  // White-labelled (subcontracted guest): drop the org's brand color too.
  const whiteLabel = useSession((s) => s.whiteLabel);
  const brand = whiteLabel ? undefined : orgs.find((o) => o.slug === activeSlug)?.primary_color;
  // Staff-only tools (AI assistant, ⌘K command palette) hit staff endpoints —
  // not mounted for guests.
  const isGuest = useSession((s) => s.role) === 'guest';

  // Expose the brand color as a CSS custom property — components opt-in by
  // referencing `var(--brand, <fallback>)`. Empty string is treated as unset.
  const style = brand ? ({ '--brand': brand } as React.CSSProperties) : undefined;

  return (
    <div className="flex min-h-screen flex-col" style={style}>
      <Header onMenuClick={() => setMobileNavOpen(true)} />

      <div className="flex flex-1">
        {/* Desktop sidebar — the header panel toggle switches between the full
            nav and a Plane-style icon rail (flyouts keep sub-pages one click
            away). The grey bg-sidebar surface frames the white content canvas. */}
        <aside
          className={cn(
            'bg-sidebar text-sidebar-foreground border-sidebar-border hidden shrink-0 border-r lg:block',
            sidebarCollapsed ? 'w-14' : 'w-64',
          )}
        >
          <div className="sticky top-14 h-[calc(100vh-3.5rem)]">
            {sidebarCollapsed ? <SidebarRail /> : <SidebarNav />}
          </div>
        </aside>

        {/* Mobile drawer */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="bg-sidebar text-sidebar-foreground w-72 p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation</SheetTitle>
            </SheetHeader>
            <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* The footer sits under the content, not under the whole row: the
            sidebar column then runs to the bottom of the page, so its sticky
            panel is never pushed up by the footer when the content height
            changes at the bottom of a long page (DEV13-116). */}
        <div className="flex min-w-0 flex-1 flex-col">
          <main className="min-w-0 flex-1 p-4 md:p-6">
            <Outlet />
          </main>
          <AppFooter />
        </div>
      </div>

      {/* Global ⌘K / Ctrl+K search + floating AI assistant — both search/act
          over staff-only endpoints, so they're not mounted for guests. */}
      {!isGuest ? (
        <>
          <CommandPalette />
          <AssistantPanel />
        </>
      ) : null}
    </div>
  );
}
